import { inject, Injectable } from '@angular/core';
import {
  mapApplicationToRawTableData,
  mapRawToApplicationTableData,
  PersistDataService,
} from '../app-state/persist-data.service';
import { TableData } from '../../table-overlay/table-overlay';
import { mapValues } from '@ps-gui/shared-utils';

@Injectable({
  providedIn: 'root',
})
export class TableOverlayStoreService {
  private readonly persistDataService = inject(PersistDataService);

  private tableDataByPageId: Record<string, TableData[]> = {};

  constructor() {
    if (!this.persistDataService.tableDataByPageId) return;
    this.tableDataByPageId = mapValues(this.persistDataService.tableDataByPageId, (_, tables) =>
      tables.map(mapRawToApplicationTableData),
    );
  }

  storeTables(tables: TableData[]) {
    for (const table of tables) {
      if (!(table.pageId in this.tableDataByPageId)) {
        this.tableDataByPageId[table.pageId] = [];
      }
      this.tableDataByPageId[table.pageId].push(table);
    }
    this.storeInAppState();
  }

  storeTable(tableData: TableData): TableData[] {
    this.storeTables([tableData]);
    return this.getTablesOnPage(tableData.pageId);
  }

  updateTableData(tableData: TableData) {
    const tablesOnPage = this.getTablesOnPage(tableData.pageId);
    const index = tablesOnPage.findIndex((table) => table.id === tableData.id);
    tablesOnPage[index] = tableData;
    this.storeInAppState();
  }

  getTablesOnPage(pageId: string): TableData[] {
    return this.tableDataByPageId[pageId] ?? [];
  }

  getTableById(tableId: string): TableData {
    const tableData = this.getAllTables().find((table) => table.id === tableId);
    if (!tableData) {
      throw new Error(`Table with id ${tableId} not found`);
    }
    return tableData;
  }

  getAllTables(): TableData[] {
    return Object.values(this.tableDataByPageId).flat();
  }

  deleteTableOverlayAtPage(pageId: string, tableId: string): TableData[] {
    const dataIndex = this.tableDataByPageId[pageId].findIndex((table) => table.id === tableId);
    if (dataIndex === -1) return this.getTablesOnPage(pageId);

    this.tableDataByPageId[pageId].splice(dataIndex, 1);
    this.storeInAppState();

    return this.getTablesOnPage(pageId);
  }

  deleteUnconfirmedTables() {
    for (const pageId in this.tableDataByPageId) {
      this.tableDataByPageId[pageId] = this.tableDataByPageId[pageId].filter((tableData) =>
        tableData.confirmed(),
      );
    }

    this.storeInAppState();
  }

  storeInAppState() {
    this.persistDataService.tableDataByPageId = mapValues(this.tableDataByPageId, (_, tables) =>
      tables.map(mapApplicationToRawTableData),
    );
  }
}
